import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../ConfirmDialog.jsx';
import '../../styles/plan-switch-review.css';

export default function PlanSwitchReviewDialog({
  review,
  changed = false,
  error = false,
  confirming = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!review) return undefined;
    const dialog = wrapperRef.current?.querySelector('[role="dialog"]');
    if (!dialog) return undefined;
    const previousFocus = document.activeElement;
    const cancelButton = dialog.querySelector('.btn-ghost');
    dialog.tabIndex = -1;
    cancelButton?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!cancelButton?.disabled) cancelButton?.click();
      } else if (event.key === 'Tab') {
        const buttons = [...dialog.querySelectorAll('button:not([disabled])')];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!first || !dialog.contains(document.activeElement)
          || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
          if (!first) dialog.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [Boolean(review)]);

  return (
    <div className="plan-switch-review-dialog" ref={wrapperRef}>
      <ConfirmDialog
        open={!!review}
        tone="warn"
        title={review ? t('plansPage.switchReviewTitle', { plan: review.targetName }) : ''}
        description={review && (
          <div className="plan-switch-review">
            <div className="plan-switch-review-price">
              <strong className="plan-switch-review-amount">
                {review.schedulerTarget ? review.totalFormatted : review.targetPriceFormatted}
              </strong>
              <span className="plan-switch-review-price-label">
                {review.schedulerTarget
                  ? t('plansPage.switchReviewSchedulerEstimate')
                  : t('plansPage.switchReviewPlanPrice')}
              </span>
              {review.schedulerTarget && (
                <span>{t('plansPage.switchReviewSchedulerCalculation', {
                  quantity: review.billedQuantity,
                  unit: review.unitFormatted,
                })}</span>
              )}
              {review.schedulerTarget && review.connectedQuantity === 0 && (
                <span>{t('plansPage.switchReviewMinimum')}</span>
              )}
            </div>
            <div className="plan-switch-review-facts">
              {review.schedulerTarget && <p>{t('plansPage.switchReviewSchedulerLimits')}</p>}
              <p>{review.isTrialing
                ? review.schedulerTarget
                  ? t('plansPage.switchReviewTrialScheduler')
                  : t('plansPage.switchReviewTrialAi', { price: review.targetPriceFormatted })
                : t('plansPage.switchReviewPaid')}</p>
              {review.cancelAtPeriodEnd && <p>{t('plansPage.switchReviewCancellationRemoved')}</p>}
            </div>
            {changed && <div role="status" className="plan-switch-review-alert">{t('plansPage.schedulerReviewChanged')}</div>}
            {error && <div role="alert" className="plan-switch-review-alert">{t('plansPage.schedulerReviewRefreshError')}</div>}
          </div>
        )}
        confirmLabel={review ? t('plansPage.switchReviewConfirm') : ''}
        confirmingLabel={t('activity.working')}
        cancelLabel={t('plansPage.schedulerReviewCancel')}
        confirming={confirming}
        confirmDisabled={confirmDisabled}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  );
}
